import { type FC } from "react";

import { DropdownInput, Option, Status, TextArea } from "@snapchat/snap-design-system";
import { Field, Form, Formik, useFormikContext } from "formik";

import { formikAdapter } from "../../shared/utils/formik";
import { useLensDiscoverabilityData } from "../hooks";
import { type LensDiscoverabilityFormValues, MAX_DESCRIPTION_LENGTH, MAX_TAGS } from "../models";

import { formField } from "./LensDiscoverabilityForm.styles";
import {
    LensDiscoverabilityDaysTimesHolidaysLabel,
    LensDiscoverabilityDaysTimesHolidaysTooltip,
    LensDiscoverabilityDescriptionLabel,
    LensDiscoverabilityDescriptionTooltip,
    LensDiscoverabilityPlacesLabel,
    LensDiscoverabilityPlacesTooltip,
    LensDiscoverabilityTagsLabel,
    LensDiscoverabilityTagsTooltip,
} from "./messages.generated";
const FormikDropdownInput = formikAdapter(DropdownInput);

const FormikTextArea = formikAdapter(TextArea);

export const LensDiscoverabilityFormik = Formik<LensDiscoverabilityFormValues>;

interface LensDiscoverabilityFormProps {
    lensOrSubmissionId: string;
}

export const LensDiscoverabilityForm: FC<LensDiscoverabilityFormProps> = ({ lensOrSubmissionId }) => {
    const {
        lensDiscoverabilityVisualTagCandidates,
        lensDiscoverabilityLocationTagCandidates,
        lensDiscoverabilityTimeTagCandidates,
        loading,
    } = useLensDiscoverabilityData(lensOrSubmissionId);

    const { errors, touched, values } = useFormikContext<LensDiscoverabilityFormValues>();

    const descriptionError = touched.description && errors.description;
    const tagsError = touched.tagsList && errors.tagsList;
    const locationsError = touched.locationsList && errors.locationsList;
    const daysTimesHolidaysError = touched.daysTimesHolidaysList && errors.daysTimesHolidaysList;

    return (
        <Form data-testid={`lens-discoverability-form`}>
            <Field
                className={formField}
                name="tagsList"
                component={FormikDropdownInput}
                label={<LensDiscoverabilityTagsLabel count={values.tagsList?.length ?? 0} total={MAX_TAGS} />}
                tooltip={<LensDiscoverabilityTagsTooltip />}
                status={tagsError}
                statusType={tagsError && Status.ERROR}
                loading={loading}
                mode="tags"
                allowClear
            >
                {lensDiscoverabilityVisualTagCandidates.map((tag) => (
                    <Option key={tag} value={tag}>
                        {tag}
                    </Option>
                ))}
            </Field>
            <Field
                className={formField}
                name="description"
                component={FormikTextArea}
                label={
                    <LensDiscoverabilityDescriptionLabel
                        count={values.description?.length ?? 0}
                        total={MAX_DESCRIPTION_LENGTH}
                    />
                }
                tooltip={<LensDiscoverabilityDescriptionTooltip />}
                status={descriptionError}
                statusType={descriptionError && Status.ERROR}
            />
            <Field
                className={formField}
                name="locationsList"
                component={FormikDropdownInput}
                label={<LensDiscoverabilityPlacesLabel count={values.locationsList?.length ?? 0} total={MAX_TAGS} />}
                tooltip={<LensDiscoverabilityPlacesTooltip />}
                status={locationsError}
                statusType={locationsError && Status.ERROR}
                loading={loading}
                mode="tags"
                allowClear
            >
                {lensDiscoverabilityLocationTagCandidates.map((tag) => (
                    <Option key={tag} value={tag}>
                        {tag}
                    </Option>
                ))}
            </Field>
            <Field
                className={formField}
                name="daysTimesHolidaysList"
                component={FormikDropdownInput}
                label={
                    <LensDiscoverabilityDaysTimesHolidaysLabel
                        count={values.daysTimesHolidaysList?.length ?? 0}
                        total={MAX_TAGS}
                    />
                }
                tooltip={<LensDiscoverabilityDaysTimesHolidaysTooltip />}
                status={daysTimesHolidaysError}
                statusType={daysTimesHolidaysError && Status.ERROR}
                loading={loading}
                mode="tags"
                allowClear
            >
                {lensDiscoverabilityTimeTagCandidates.map((tag) => (
                    <Option key={tag} value={tag}>
                        {tag}
                    </Option>
                ))}
            </Field>
        </Form>
    );
};
